/**
 * useEntitlements — fetches /api/entitlements/me and exposes a stable
 * feature-access map the rest of the app can use to gate UI.
 *
 * Server-side enforcement is the source of truth (every protected route
 * runs `require_feature(...)` and returns 403 with bilingual upgrade
 * hints). This hook is *only* for UX polish:
 *   • hide sidebar entries the user can't reach
 *   • route to an "upgrade required" page when they try anyway
 *   • show trial-expiry banners
 *
 * Usage:
 *   const ent = useEntitlements();
 *   if (ent.can('manager_review_queue')) {  ... }
 */
import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  ready: false,
  plan_group: null,
  security_role: null,
  entitlement_source: null,
  trial_active: false,
  trial_ends_at: null,
  is_admin: false,
  downgraded: false,
  prior_plan_group: null,
  features: {},
  can: () => false,
  canEdit: () => false,
  preserved: () => false,
  hint: () => ({ en: '', ja: '' }),
};

function buildState(data) {
  return {
    ready: true,
    plan_group: data.plan_group,
    security_role: data.security_role,
    entitlement_source: data.entitlement_source,
    trial_active: !!data.trial_active,
    trial_ends_at: data.trial_ends_at,
    is_admin: !!data.is_admin,
    downgraded: !!data.downgraded,
    prior_plan_group: data.prior_plan_group || null,
    features: data.features || {},
    can: (key) => !!data.features?.[key]?.can_view,
    canEdit: (key) => !!data.features?.[key]?.can_edit,
    // True when the feature is visible ONLY because this is a downgraded
    // former-premium account → render it read-only with a "paused" banner.
    preserved: (key) => !!data.features?.[key]?.preserved,
    hint: (key) => ({
      en: data.features?.[key]?.upgrade_hint_en || '',
      ja: data.features?.[key]?.upgrade_hint_ja || '',
    }),
  };
}

// Session-scoped shared cache. `/api/entitlements/me` is pure UX gating that
// is identical for every component and rarely changes mid-session, yet it was
// previously fetched on EVERY mount of EVERY component using this hook
// (DashboardLayout + each EntitlementGate-wrapped route + pages), producing
// 4–11 redundant identical GETs per navigation. We now resolve a single
// in-flight promise and reuse the cached result for the rest of the session.
let _cachedState = null;
let _inflight = null;

function loadEntitlements() {
  if (_cachedState) return Promise.resolve(_cachedState);
  if (_inflight) return _inflight;
  _inflight = fetch(`${API}/api/entitlements/me`, { credentials: 'include' })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      _inflight = null;
      if (!data) return null;
      _cachedState = buildState(data);
      return _cachedState;
    })
    .catch(() => { _inflight = null; return null; });
  return _inflight;
}

// Call after an action that can change a user's plan/entitlements (e.g. plan
// upgrade, account-type switch) so the next read re-fetches fresh data.
export function invalidateEntitlements() {
  _cachedState = null;
  _inflight = null;
}

export function useEntitlements() {
  const [state, setState] = useState(_cachedState || EMPTY);

  useEffect(() => {
    // Already initialized from the module cache via the useState initializer —
    // nothing to re-set synchronously here.
    if (_cachedState) return undefined;
    let cancelled = false;
    loadEntitlements().then((s) => {
      if (!cancelled && s) setState(s);
    });
    return () => { cancelled = true; };
  }, []);

  return state;
}
